import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import { Flex, FlexBehavior, FlexAlignItems, magma } from 'react-magma-dom';
import { OpenInNewIcon } from 'react-magma-icons';
import { LeadParagraph } from '../../components/LeadParagraph';
import { StorybookIcon } from '../../images/icons/storybook-icon';
export const MagmaChartsSiteLink = () => <Link to="https://storybook-preview-dev--upbeat-sinoussi-f675aa.netlify.app/?path=/story/carbonchart" target="_blank" mdxType="Link">
    <Flex behavior={FlexBehavior.container} alignItems={FlexAlignItems.center} style={{
    backgroundColor: magma.colors.neutral200,
    width: 'fit-content',
    gap: magma.spaceScale.spacing03,
    padding: `${magma.spaceScale.spacing04} ${magma.spaceScale.spacing05}`,
    borderRadius: magma.spaceScale.spacing03
  }} mdxType="Flex">
      <StorybookIcon mdxType="StorybookIcon" />
      <span>Magma Charts demo site</span>
      <OpenInNewIcon size={magma.iconSizes.small} mdxType="OpenInNewIcon" />
    </Flex>
  </Link>;
export const _frontmatter = {
  "title": "Getting Started",
  "order": 1
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const Alert = makeShortcode("Alert");
const SimplePropsTable = makeShortcode("SimplePropsTable");
const Link = makeShortcode("Link");
const layoutProps = {
  MagmaChartsSiteLink,
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <PageContent componentName="getting_started" type="data_visualization" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  Magma charts enable you to convey precise and compelling narratives around
  data through visually appealing and accessible visualizations. 
      </LeadParagraph>
      <p>{`Magma Charts is based on `}<a parentName="p" {...{
          "href": "https://charts.carbondesignsystem.com/introduction"
        }}>{`Carbon Charts from IBM`}</a>{`.`}</p>
      <p>{`Get started below with implementation instructions for developers. You can also learn more about the `}<a parentName="p" {...{
          "href": "../chart-types"
        }}>{`chart types`}</a>{` we provide, as well as `}<a parentName="p" {...{
          "href": "../chart-demos"
        }}>{`view examples`}</a>{`.`}</p>
      <MagmaChartsSiteLink mdxType="MagmaChartsSiteLink" />
      <h2>{`Installation`}</h2>
      <Alert variant="warning" mdxType="Alert">
  <strong>Important!</strong> CarbonChart is imported from{' '}
  <inlineCode>@react-magma/charts</inlineCode> which must be installed as a peer
  dependency.
      </Alert>
      <h3>{`Setup Requirements`}</h3>
      <p>{`Your project needs to be running `}<strong parentName="p">{`React v17`}</strong></p>
      <h3>{`Install the Charts Library`}</h3>
      <p>{`Install the React Magma Charts library with `}<inlineCode parentName="p">{`npm`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-sh",
          "metastring": "title noRender noCodeSandbox",
          "title": true,
          "noRender": true,
          "noCodeSandbox": true
        }}>{`npm install --save @react-magma/charts
`}</code></pre>
      <p>{`Install peer dependencies`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-sh",
          "metastring": "title noRender noCodeSandbox",
          "title": true,
          "noRender": true,
          "noCodeSandbox": true
        }}>{`npm install --save \\
    "@emotion/react@^11.13.0" \\
    "@emotion/styled@^11.13.0" \\
    "react@^17.0.2" \\
    "react-dom@^17.0.2" \\
    "react-magma-dom@^4.7.0-next.1" \\
    "react-magma-icons@^3.0.0" \\
    "@react-magma/charts@^9.0.0"
`}</code></pre>
      <h2>{`Accessible chart toolbar`}</h2>
      <p>{`Carbon Charts ships with built-in toolbar buttons (Show as Table, Fullscreen, More Options) that have several WCAG 2.2 violations:`}</p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`aria-haspopup="true"`}</inlineCode>{` is used incorrectly (it should be `}<inlineCode parentName="li">{`"dialog"`}</inlineCode>{` for the table button and absent on the fullscreen button)`}</li>
        <li parentName="ul">{`Focus does not move into the modal when "Show as Table" is activated`}</li>
        <li parentName="ul">{`The table dialog lacks a semantic heading`}</li>
        <li parentName="ul">{`The chart title uses `}<inlineCode parentName="li">{`<p role="heading">`}</inlineCode>{` instead of a semantic heading element`}</li>
        <li parentName="ul">{`Screen readers announce button labels redundantly`}</li>
        <li parentName="ul">{`The "Show as table" label is not descriptive enough`}</li>
      </ul>
      <p>{`The `}<inlineCode parentName="p">{`chartToolbar`}</inlineCode>{` prop on `}<inlineCode parentName="p">{`CarbonChart`}</inlineCode>{` replaces Carbon's toolbar with accessible Magma components. When provided, Carbon's built-in toolbar is `}<strong parentName="p">{`automatically disabled`}</strong>{` and the chart title is rendered as a semantic heading. It defaults to `}<inlineCode parentName="p">{`<h2>`}</inlineCode>{` and can be configured from `}<inlineCode parentName="p">{`<h1>`}</inlineCode>{` through `}<inlineCode parentName="p">{`<h6>`}</inlineCode>{` with `}<inlineCode parentName="p">{`chartToolbar.titleLevel`}</inlineCode>{`.`}</p>
      <h3>{`Basic usage`}</h3>
      <p>{`Pass a `}<inlineCode parentName="p">{`chartToolbar`}</inlineCode>{` object to `}<inlineCode parentName="p">{`CarbonChart`}</inlineCode>{`. The title is read from `}<inlineCode parentName="p">{`options.title`}</inlineCode>{`. This renders the accessible toolbar with "Show as table", "Fullscreen", and "More options" buttons. The "More options" dropdown includes built-in "Download as CSV", "Download as PNG", and "Download as JPG" items.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { CarbonChart, CarbonChartType } from '@react-magma/charts';
import { Card } from 'react-magma-dom';

export function Example() {
  return (
    <Card style={{ padding: '12px' }}>
      <CarbonChart
        type={CarbonChartType.donut}
        dataSet={[
          { group: 'High performance', value: 50 },
          { group: 'Average performance', value: 30 },
          { group: 'Poor performance', value: 15 },
          { group: 'Not attempted', value: 5 },
        ]}
        options={{
          title: 'Overall Activity Performance',
          resizable: true,
          height: '400px',
          donut: {
            center: { label: 'Questions' },
          },
          legend: {
            truncation: { type: 'none' },
          },
        }}
        chartToolbar={{}}
      />
    </Card>
  );
}
`}</code></pre>
      <p>{`See `}<a parentName="p" {...{
          "href": "../chart-demos"
        }}>{`Chart Demos`}</a>{` for examples of every chart type with the accessible toolbar enabled, and the `}<a parentName="p" {...{
          "href": "../chart-demos#toolbar_customization"
        }}>{`Toolbar customization`}</a>{` section for advanced configurations (custom menu items, table columns, chart title heading level, custom content around the title and between the title and the chart, button visibility, inverse theme).`}</p>
      <h3>{`Content between the title and the chart`}</h3>
      <p><inlineCode parentName="p">{`additionalContent`}</inlineCode>{` is a prop on `}<inlineCode parentName="p">{`CarbonChart`}</inlineCode>{` itself rather than on `}<inlineCode parentName="p">{`chartToolbar`}</inlineCode>{`. It renders below the toolbar row and above the chart, which is where interactive controls such as filters belong — the title row only has space for inline content the height of an icon.`}</p>
      <p>{`It requires `}<inlineCode parentName="p">{`chartToolbar`}</inlineCode>{`, changes how the chart's total height is composed, and needs `}<inlineCode parentName="p">{`options.height`}</inlineCode>{` set. See `}<a parentName="p" {...{
          "href": "../chart-demos#custom_content_between_the_title_and_the_chart"
        }}>{`Custom content between the title and the chart`}</a>{` for the reasoning and a working example.`}</p>
      <h3>{`Chart toolbar props`}</h3>
      <p>{`The chart title is read from `}<inlineCode parentName="p">{`options.title`}</inlineCode>{` (the standard Carbon Charts title option) and rendered as a semantic heading element, replacing Carbon's inaccessible `}<inlineCode parentName="p">{`<p role="heading">`}</inlineCode>{`. It defaults to `}<inlineCode parentName="p">{`<h2>`}</inlineCode>{`; set `}<inlineCode parentName="p">{`titleLevel`}</inlineCode>{` to match the surrounding page hierarchy.`}</p>
      <SimplePropsTable propertyValues={{
        showAsTable: {
          type: {
            name: 'boolean'
          },
          required: false,
          description: 'Renders the "Show as table" button and modal.',
          defaultValue: 'true'
        },
        fullscreen: {
          type: {
            name: 'boolean'
          },
          required: false,
          description: 'Renders the fullscreen toggle button.',
          defaultValue: 'true'
        },
        moreOptions: {
          type: {
            name: 'ReactNode'
          },
          required: false,
          description: 'Additional DropdownMenuItem elements appended below the built-in CSV/PNG/JPG download items.',
          defaultValue: 'undefined'
        },
        tableColumns: {
          type: {
            name: 'ChartDataTableColumn[]'
          },
          required: false,
          description: 'Column definitions for the data table. If omitted, columns are auto-derived from the dataset object keys.',
          defaultValue: 'undefined'
        },
        tableHeaderLabel: {
          type: {
            name: 'string'
          },
          required: false,
          description: 'First line of the modal heading.',
          defaultValue: "'Tabular representation'"
        },
        tableHeaderLevel: {
          type: {
            name: '1 | 2 | 3 | 4 | 5 | 6'
          },
          required: false,
          description: 'Number to indicate which level heading will render (e.g. h1, h2 etc.)',
          defaultValue: '2'
        },
        titleLevel: {
          type: {
            name: '1 | 2 | 3 | 4 | 5 | 6'
          },
          required: false,
          description: 'Number to indicate which heading level will render for the chart title (e.g. h1, h2 etc.)',
          defaultValue: '2'
        },
        titlePrefix: {
          type: {
            name: 'ReactNode'
          },
          required: false,
          description: 'Custom content rendered inline immediately before the chart title.',
          defaultValue: 'undefined'
        },
        titleSuffix: {
          type: {
            name: 'ReactNode'
          },
          required: false,
          description: 'Custom content rendered inline between the chart title and the toolbar actions. The chart equivalent of the additionalContent prop on Input and Select.',
          defaultValue: 'undefined'
        }
      }} mdxType="SimplePropsTable" />
      <h3>{`Standalone components`}</h3>
      <p>{`For adopters who need more granular control outside of `}<inlineCode parentName="p">{`CarbonChart`}</inlineCode>{`, the following components are also exported from `}<inlineCode parentName="p">{`@react-magma/charts`}</inlineCode>{`:`}</p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`ChartTableButton`}</inlineCode>{` — Accessible "Show as table" trigger with `}<inlineCode parentName="li">{`aria-haspopup="dialog"`}</inlineCode></li>
        <li parentName="ul"><inlineCode parentName="li">{`ChartFullscreenButton`}</inlineCode>{` — Fullscreen toggle (no `}<inlineCode parentName="li">{`aria-haspopup`}</inlineCode>{`)`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`ChartMoreOptionsButton`}</inlineCode>{` — Dropdown wrapper with `}<inlineCode parentName="li">{`MoreVertIcon`}</inlineCode></li>
        <li parentName="ul"><inlineCode parentName="li">{`ChartTableModal`}</inlineCode>{` — Focus-trapped modal with semantic heading, data table, and "Download as CSV" button`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`ChartDataTable`}</inlineCode>{` — Renders chart data in a Magma `}<inlineCode parentName="li">{`Table`}</inlineCode>{` with auto-derived columns`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`ChartToolbar`}</inlineCode>{` — Flex layout wrapper with heading and action slot`}</li>
      </ul>
      <h3>{`Accessibility`}</h3>
      <p>{`The toolbar addresses the following WCAG 2.2 guidelines:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`1.3.1 Info and Relationships (Level A)`}</strong>{` — Chart title uses a semantic `}<inlineCode parentName="li">{`<h1>`}</inlineCode>{`–`}<inlineCode parentName="li">{`<h6>`}</inlineCode>{` (default `}<inlineCode parentName="li">{`<h2>`}</inlineCode>{`) instead of `}<inlineCode parentName="li">{`<p role="heading">`}</inlineCode>{`. Modal has a semantic heading. Table uses `}<inlineCode parentName="li">{`<th>`}</inlineCode>{` elements.`}</li>
        <li parentName="ul"><strong parentName="li">{`2.4.3 Focus Order (Level A)`}</strong>{` — Focus moves into the modal when it opens, and returns to the trigger button when it closes.`}</li>
        <li parentName="ul"><strong parentName="li">{`2.4.6 Headings and Labels (Level AA)`}</strong>{` — "Show as table" button uses the chart title as its accessible label. Button labels are descriptive and unique.`}</li>
        <li parentName="ul"><strong parentName="li">{`4.1.2 Name, Role, Value (Level A)`}</strong>{` — Correct `}<inlineCode parentName="li">{`aria-haspopup="dialog"`}</inlineCode>{` on the table button. No `}<inlineCode parentName="li">{`aria-haspopup`}</inlineCode>{` on the fullscreen button. `}<inlineCode parentName="li">{`aria-expanded`}</inlineCode>{` reflects modal state.`}</li>
      </ul>
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      